import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {useForceUpdate} from '@containers/hooks'
import {cn} from '@containers/html-utils'

export type PanelResizerType = 'left' | 'right'
export type LineWidth = 'w-[5px]' | 'w-[2px]'

const TwOverlay = tw.div`
  fixed
  top-0
  left-0
  right-0
  bottom-0
  bg-transparent
  z-10
  cursor-col-resize
`

const TwResizer = tw.div<{
  $dragged?: boolean
  $type: PanelResizerType
  $lineWidth?: LineWidth
}>`
  absolute
  top-0
  bottom-0
  bg-action-default
  transition-opacity
  hover:opacity-100
  hover:delay-75
  duration-150
  cursor-col-resize
  z-50

  ${p => p.$lineWidth ?? 'w-[5px]'}
  ${p => cn(p.$dragged ? 'opacity-100' : 'opacity-0')}
  ${p => {
    if (p.$type === 'left') {
      return '-left-[3px]'
    }

    if (p.$type === 'right') {
      return '-right-[3px]'
    }

    return ''
  }}
`
const TwResizerPlaceholder = tw.div<{
  $type: PanelResizerType
  $lineWidth?: LineWidth
  $offset?: string
}>`
  absolute
  top-0
  bottom-0
  bg-border-base
  z-[49]

  ${p => p.$lineWidth ?? 'w-[2px]'}
  ${p => {
    if (p.$offset) {
      return p.$offset
    }

    if (p.$type === 'left') {
      return '-left-[3px]'
    }

    if (p.$type === 'right') {
      return '-right-[3px]'
    }

    return ''
  }}
`

interface Props {
  initialWidth: number
  lineWidth?: LineWidth
  minWidth: number
  maxWidth: number
  onStartResize?: () => void
  onResize: (width: number) => void
  onEndResize: (width: number) => void
  dragAllowed: boolean
  type?: PanelResizerType
  offset?: string
  placeholder?: boolean
  dragged?: boolean
  setDragged?: boolean
}

export const PanelResizer = (props: Props) => {
  const [width, setWidth] = useState(props.initialWidth)

  useEffect(() => {
    setWidth(props.initialWidth)
  }, [props.initialWidth])

  const draggedRef = useRef(false)
  const forceUpdate = useForceUpdate()

  const startDragXRef = useRef(0)

  const getWidthWithBounds = (width: number): number => {
    const {minWidth, maxWidth} = props
    return Math.max(minWidth, Math.min(maxWidth, width))
  }

  const getType = () => {
    return props.type ?? 'left'
  }

  const dragStart = (e: React.MouseEvent<HTMLElement>) => {
    if (!props.dragAllowed) {
      return
    }

    props.onStartResize?.()
    draggedRef.current = true
    startDragXRef.current = e.clientX
    document.body.classList.add('no-select')
    document.body.classList.add('body--chat-resize')
    document.addEventListener('mouseup', dragStop)
    document.addEventListener('mousemove', mouseMove)

    forceUpdate()
  }

  const mouseMove = (e: MouseEvent) => {
    if (!draggedRef.current) {
      return
    }

    const width = getWidthWithBounds(getWidth(e))
    requestAnimationFrame(() => {
      props.onResize(width)
    })
  }

  const dragStop = (e: MouseEvent) => {
    if (!draggedRef.current) {
      return
    }

    const width = getWidthWithBounds(getWidth(e))
    setWidth(width)
    draggedRef.current = false

    document.removeEventListener('mousemove', mouseMove)
    document.removeEventListener('mouseup', dragStop)
    props.onEndResize(width)

    setTimeout(() => {
      document.body.classList.remove('no-select')
      document.body.classList.remove('body--chat-resize')
    }, 200)

    forceUpdate()
  }
  const getWidth = (e: MouseEvent): number => {
    if (getType() === 'left') {
      return width - e.clientX + startDragXRef.current
    }

    if (getType() === 'right') {
      return width + e.clientX - startDragXRef.current
    }

    return 0
  }

  const type = getType()

  return (
    <>
      <TwResizer
        $dragged={props.dragged ?? draggedRef.current}
        onMouseDown={dragStart}
        $type={type}
        $lineWidth={props.lineWidth}
      />
      {props.placeholder && (
        <TwResizerPlaceholder
          $type={getType()}
          $lineWidth={props.lineWidth}
          $offset={props.offset}
        />
      )}
      {(props.dragged ?? draggedRef.current) && <TwOverlay />}
    </>
  )
}
